// src/components/AdminNavbar.js
import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  IconButton,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';

import {
  Menu as MenuIcon,
  Home as HomeIcon,
  People as PeopleIcon,
  SupervisorAccount as AdminIcon,
  Business as BusinessIcon,
  Lock as PermissionsIcon,
  Apartment as TenantIcon,
  AssignmentInd as UserCompanyIcon,
  Settings as UserPermissionsIcon,
  VpnLock as BannedIpIcon,
  ExitToApp as LogoutIcon,
  VpnKey as ServiceKeyIcon,
  Schedule as ScheduleIcon,
  ReceiptLong as LogsIcon,
} from '@mui/icons-material';

import { useTheme } from '@mui/material/styles';
import { useAuth } from '../context/AuthContext';
import logo from './logo.png';

const menuItems = [
  { label: 'Stats', to: '/stats', icon: <ScheduleIcon fontSize="small" /> }, // ✅ nuevo
  { label: 'Usuarios', to: '/accounts', icon: <PeopleIcon fontSize="small" /> },
  { label: 'Admins', to: '/admins', icon: <AdminIcon fontSize="small" /> },
  { label: 'Permisos', to: '/permissions', icon: <PermissionsIcon fontSize="small" /> },
  { label: 'Planes', to: '/plans', icon: <BusinessIcon fontSize="small" /> },
  { label: 'Tenants', to: '/tenants', icon: <TenantIcon fontSize="small" /> },
  { label: 'Usr/Tenants', to: '/usertenants', icon: <UserCompanyIcon fontSize="small" /> },
  { label: 'IPs Bann', to: '/bannedips', icon: <BannedIpIcon fontSize="small" /> },
  { label: 'Logs', to: '/logs', icon: <LogsIcon fontSize="small" /> },
  { label: 'Service-Keys', to: '/service_keys', icon: <ServiceKeyIcon fontSize="small" /> },
  { label: 'Scheduler', to: '/scheduler', icon: <ScheduleIcon fontSize="small" /> },
];

function pickActivePath(pathname) {
  // Match por prefijo, pero cuidando "/" para que no matchee todo
  if (pathname === '/') return '/';
  const match = menuItems
    .filter(i => i.to !== '/')
    .find(i => pathname === i.to || pathname.startsWith(i.to + '/'));
  return match?.to || false;
}

const AdminNavbar = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [drawerOpen, setDrawerOpen] = useState(false);

  const activeValue = useMemo(() => pickActivePath(location.pathname), [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const Brand = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mr: 2 }}>
      <Box component="img" src={logo} alt="IntegraNube Admin" sx={{ height: 30 }} />
    </Box>
  );

  return (
    <AppBar position="sticky" elevation={1}>
      <Toolbar sx={{ gap: 1 }}>
        {isMobile && (
          <IconButton color="inherit" onClick={() => setDrawerOpen(true)} edge="start">
            <MenuIcon />
          </IconButton>
        )}

        {Brand}

        {!isMobile && (
          <Tabs
            value={activeValue}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            textColor="inherit"
            indicatorColor="secondary"
            sx={{
              flex: 1,
              minHeight: 48,
              '& .MuiTab-root': {
                minHeight: 48,
                textTransform: 'none',
                fontWeight: 600,
                opacity: 0.9,
              },
              '& .MuiTab-root.Mui-selected': { opacity: 1 },
            }}
          >
            {menuItems.map(item => (
              <Tab
                key={item.to}
                value={item.to}
                icon={item.icon}
                iconPosition="start"
                label={item.label}
                component={RouterLink}
                to={item.to}
              />
            ))}
          </Tabs>
        )}

        <Box sx={{ flexGrow: isMobile ? 1 : 0 }} />

        <Tooltip title="Cerrar sesión">
          <Button
            color="inherit"
            onClick={handleLogout}
            startIcon={<LogoutIcon />}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Salir
          </Button>
        </Tooltip>

        <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box sx={{ width: 300 }}>
            <Box sx={{ px: 2, py: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box component="img" src={logo} alt="IntegraNube Admin" sx={{ height: 28 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                InAdmin
              </Typography>
            </Box>
            <Divider />
            <List>
              {menuItems.map(item => (
                <ListItemButton
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  selected={activeValue === item.to}
                  onClick={() => setDrawerOpen(false)}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Drawer>
      </Toolbar>
    </AppBar>
  );
};

export default AdminNavbar;
